import React, { useState } from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import css from './FAQ.module.css';

/**
 * FAQ component that displays a list of frequently asked questions
 * Only one FAQ can be open at a time with smooth animations
 *
 * @param {Object} props
 * @param {Array} props.faqData - Array of FAQ objects with key, queId, ansId properties
 * @param {string} [props.rootClassName] - Custom class for the root element
 * @param {string} [props.className] - Additional class for the root element
 * @returns {JSX.Element}
 */
export const FAQ = props => {
  const { faqData = [], rootClassName, className } = props;
  const [openFaqKey, setOpenFaqKey] = useState(null);

  const classes = classNames(rootClassName || css.root, className);

  const handleFaqToggle = key => {
    setOpenFaqKey(openFaqKey === key ? null : key);
  };

  if (!faqData || faqData.length === 0) {
    return null;
  }

  return (
    <div className={classes}>
      <h3 className={css.faqTitle}>
        <FormattedMessage id="FAQ.title" />
      </h3>
      <div className={css.faqList}>
        {faqData.map(faq => {
          const isOpen = openFaqKey === faq.key;
          const faqClasses = classNames(css.faqItem, {
            [css.faqItemOpen]: isOpen,
          });

          return (
            <div key={faq.key} className={faqClasses}>
              <button
                type="button"
                className={css.faqQuestion}
                onClick={() => handleFaqToggle(faq.key)}
                aria-expanded={isOpen}
                aria-controls={`faq-answer-${faq.key}`}
              >
                <span className={css.questionText}>
                  <FormattedMessage id={faq.queId} />
                </span>
                <span
                  className={classNames(css.toggleIcon, {
                    [css.toggleIconOpen]: isOpen,
                  })}
                >
                  {isOpen ? '-' : '+'}
                </span>
              </button>
              <div
                id={`faq-answer-${faq.key}`}
                className={classNames(css.faqAnswer, {
                  [css.faqAnswerOpen]: isOpen,
                })}
                aria-hidden={!isOpen}
              >
                <div className={css.answerContent}>
                  <FormattedMessage id={faq.ansId} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FAQ;
